import {
  collection,
  query,
  addDoc,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { createContext, useContext, useEffect, useState } from "react";
import { FirebaseContext } from "./FirebaseContext";
import { BusinessContext } from "./BusinessContext";
import { IBusiness } from "../interfaces/IBusiness";

export interface IEventsProps {
  eventsBmc: IEventsBmc[];
  eventsWebBmc: IEvents[];
  addEventBmc: (data: IEventsBmc) => Promise<void>;
  deleteEventBmc: (id: string) => Promise<void>;
  addEventWebBmc: (data: IEventsBmc) => Promise<void>;
  deleteEventWebBmc: (id: string) => Promise<void>;
  events: IEvents[];
  addEvent: (data: IEvents) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  loading: boolean;
  updateTEventBMC: (item: IEventsBmc) => Promise<void>;
  updateTEventBusiness: (item: IEvents) => Promise<void>;
  getEventsBmc: (pageSize?: number, lastDoc?: any) => Promise<any[]>;
  getEventsBusiness: (pageSize?: number, lastDoc?: any) => Promise<any[]>;
  getEventsWebBmc: (pageSize?: number, lastDoc?: any) => Promise<any[]>;
}

export type IEventsBmc = {
  id?: string;
  businessId?: string;
  title: string;
  description: string;
  description2?: string;
  description3?: string;
  initTimestamp: number;
  finishTimestamp: number;
  hour?: string;
  allDay?: boolean;
  image: any;
  sliderVisible?: boolean;
  test?: boolean;
  externalUrl?: string;
};

export type IEvents = {
  id?: string;
  businessId?: string;
  title: string;
  description: string;
  initTimestamp: number;
  finishTimestamp: number;
  hour?: string;
  allDay?: boolean;
  isOffer?: boolean;
  image: any;
  businessData: IBusiness;
  developer?: boolean;
  externalUrl?: string;
  phone?: string;
  local?: string;
};

export const EventsContext = createContext<IEventsProps>(null);

export const EventsProvider: any = ({ children }: any) => {
  const [eventsBmc, setEventsBmc] = useState<IEventsBmc[]>([]);
  const [eventsWebBmc, setEventsWebBmc] = useState<IEvents[]>([]);
  const [events, setEvents] = useState<IEvents[]>([]);
  const [loading, setLoading] = useState(true);

  const { getBusinessById } = useContext(BusinessContext);
  const { db, user } = useContext(FirebaseContext);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const now = new Date(yesterday).setHours(23, 59, 0, 0);

  // 🚀 FASE 3: Cache de empresas para evitar N+1 queries
  const businessCacheRef = { current: new Map<string, IBusiness>() };

  /** 🔹 Crear evento BMC */
  const addEventBmc = async (data: IEventsBmc) => {
    await addDoc(collection(db, "events-bmc"), { ...data });
  };

  /** 🔹 Crear evento Web BMC */
  const addEventWebBmc = async (data: IEventsBmc) => {
    await addDoc(collection(db, "events-bmc-web"), { ...data });
  };

  /** 🔹 Crear evento Business */
  const addEvent = async (data: IEvents) => {
    await addDoc(collection(db, "events-business"), { ...data });
  };

  /** 🔹 Eliminar */
  const deleteEventBmc = async (id: string) => {
    await deleteDoc(doc(db, "events-bmc", id));
  };
  const deleteEventWebBmc = async (id: string) => {
    await deleteDoc(doc(db, "events-bmc-web", id));
  };
  const deleteEvent = async (id: string) => {
    await deleteDoc(doc(db, "events-business", id));
  };

  /** 🔹 Obtener eventos BMC con paginación */
  const getEventsBmc = async (pageSize = 20, lastDoc?: any) => {
    const q = query(
      collection(db, "events-bmc"),
      where("finishTimestamp", ">=", now),
      orderBy("finishTimestamp", "asc"),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      limit(pageSize)
    );

    const snap = await getDocs(q);
    const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as IEventsBmc[];
    setEventsBmc((prev) => (lastDoc ? [...prev, ...arr] : arr));
    setLoading(false);
    return snap.docs;
  };

  /** 🔹 Obtener eventos Business con paginación */
  const getEventsBusiness = async (pageSize = 20, lastDoc?: any) => {
    const q = query(
      collection(db, "events-business"),
      where("finishTimestamp", ">=", now),
      orderBy("finishTimestamp", "asc"),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      limit(pageSize)
    );

    const snap = await getDocs(q);
    
    // 🚀 FASE 3: Usar cache para evitar lecturas duplicadas de business
    const arr = await Promise.all(
      snap.docs.map(async (d) => {
        const payload = d.data() as IEvents;
        
        // Verificar cache primero
        let biz = businessCacheRef.current.get(payload.businessId);
        if (!biz) {
          biz = await getBusinessById(payload.businessId);
          businessCacheRef.current.set(payload.businessId, biz);
        }
        
        return { id: d.id, ...payload, businessData: biz } as IEvents;
      })
    );
    setEvents((prev) => (lastDoc ? [...prev, ...arr] : arr));
    setLoading(false);
    return snap.docs;
  };

  /** 🔹 Obtener eventos Web BMC con paginación */
  const getEventsWebBmc = async (pageSize = 20, lastDoc?: any) => {
    const q = query(
      collection(db, "events-bmc-web"),
      where("finishTimestamp", ">=", now),
      orderBy("finishTimestamp", "asc"),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      limit(pageSize)
    );

    const snap = await getDocs(q);
    
    // 🚀 FASE 3: Usar cache para evitar lecturas duplicadas de business
    const arr = await Promise.all(
      snap.docs.map(async (d) => {
        const data = d.data() as IEventsBmc;
        
        // Verificar cache primero
        let biz = businessCacheRef.current.get(data.businessId);
        if (!biz) {
          biz = await getBusinessById(data.businessId);
          businessCacheRef.current.set(data.businessId, biz);
        }
        
        return { id: d.id, ...data, businessData: biz } as IEvents;
      })
    );
    setEventsWebBmc((prev) => (lastDoc ? [...prev, ...arr] : arr));
    setLoading(false);
    return snap.docs;
  };

  /** 🔹 Actualizar */
  const updateTEventBMC = async (item: IEventsBmc) => {
    await updateDoc(doc(db, "events-bmc", item.id), { ...item });
  };
  const updateTEventBusiness = async (item: IEvents) => {
    await updateDoc(doc(db, "events-business", item.id), { ...item });
  };

  /** 🔹 FASE 3: NO cargar automáticamente 
   * Las páginas específicas llamarán a las funciones cuando las necesiten
   * Esto ahorra ~100 reads por usuario que no ve eventos
   */
  // useEffect removido - carga condicional por página

  return (
    <EventsContext.Provider
      value={{
        eventsBmc,
        addEventBmc,
        loading,
        deleteEventBmc,
        events,
        addEvent,
        deleteEvent,
        addEventWebBmc,
        deleteEventWebBmc,
        eventsWebBmc,
        updateTEventBMC,
        updateTEventBusiness,
        getEventsBmc,
        getEventsBusiness,
        getEventsWebBmc,
      }}
    >
      {children}
    </EventsContext.Provider>
  );
};
